import { notify } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import { TFunction } from 'i18next'
import { useCallback, useState } from 'react'
import { ErrorCode, FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import { uploadFile } from '@features/attachments/query-api'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { QueryKeys } from '../../../enums/queryKeys'
import { useSegment } from '../../../hooks'
import { useIsAutoCegidIntegrationEnabled } from '../../../hooks/useIsAutoCegidIntegrationEnabled'
import { createImportedInvoice } from '../query-api'
import { getImportedUploadErrorMessage } from '../utils/getImportedUploadErrorMessage'
import { getImportedUploadRejectionMessage } from '../utils/getImportedUploadRejectionMessage'

interface UploadProgress {
  current: number
  total: number
}

interface UseUploadImportedInvoicesResult {
  isUploading: boolean
  progress: UploadProgress
  uploadFiles: (filesAccepted: File[], filesRejected?: FileRejection[]) => void
}

const uploadProgressInitialData: UploadProgress = {
  current: 0,
  total: 0,
}

/**
 * STR-138 / S6 — imported (externally-issued) sales-invoice upload.
 *
 * Unlike the expenses upload (`useUploadFiles`), this deliberately does NOT send
 * `x-create-attachment` / `x-should-scan`, so no expenses Attachment/Bill is
 * created and Bohr's synchronous OCR scan never runs. A successful upload therefore
 * only means the file was stored, never that its contents have been read.
 *
 * We reuse the multi-file progress loop from `useUploadFiles` but keep a dedicated
 * hook to avoid its expenses-only side effects (e.g. the CPA-attachment info modal
 * and Auto-Cegid integration logic).
 *
 * Upload marker (S19 / STR-203): every upload carries `x-imported-sales-invoice`,
 * which scopes the file as an imported sales invoice on billy-api (origin + org),
 * without a scan or attachment. The agentic ingestion pipeline is dispatched
 * (`x-dispatch-agentic-document`) ONLY on the M1b / non-autoforward path. For
 * shoebox auto-forward orgs we send the marker WITHOUT dispatch (store-only),
 * because rest-invoice forwards the document to Cegid itself.
 *
 * STR-158 / S12 — after each stored upload reply, create the imported (shoebox)
 * document shell in rest-invoice keyed by the returned `fileId`, passing the
 * FE-computed `autoForwardToCegid` (`useIsAutoCegidIntegrationEnabled`). This makes
 * the uploaded row appear on the imported-invoices page and, for auto-Cegid-eligible
 * orgs, rest-invoice auto-forwards it.
 */
export const useUploadImportedInvoices = (): UseUploadImportedInvoicesResult => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { isAutoCegidIntegrationEnabled } = useIsAutoCegidIntegrationEnabled()
  const { track } = useSegment()
  const queryClient = useQueryClient()
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>(uploadProgressInitialData)

  const handleMultipleFilesMutation = useCallback(
    async ({ filesAccepted, filesRejected }: { filesAccepted: File[]; filesRejected?: FileRejection[] }) => {
      // The design's oversize toast is one filename-less sentence, which can only
      // name the offending file when the whole selection *was* that file. Every
      // other shape (mixed selection, several rejections, a lone invalid type) keeps
      // the grouped `filename: reason` toast, which is the only form that says which
      // file failed.
      const loneOversizeRejection = getLoneOversizeRejection({ filesAccepted, filesRejected })

      if (loneOversizeRejection) {
        notify({
          id: NotificationKeys.ImportedInvoicesUploadRejection,
          message: getImportedUploadRejectionMessage(loneOversizeRejection, t),
          variant: 'error',
        })

        return
      }

      // Start with any dropzone-rejected files (invalid type/size). The DS
      // dropzone/file button already translates each `error.message` in place.
      const fileErrors: string[] = getRejectedFilesMessages(filesRejected, t)

      if (filesAccepted.length > 0 && !organization?.id) {
        notify({
          id: NotificationKeys.ImportedInvoicesUpload,
          message: t('file.rejected'),
          variant: 'error',
        })
        trackError('"organizationId" has to be provided to upload an imported invoice')

        // Still surface any dropzone-rejected files so a mixed selection (a valid
        // file alongside invalid ones) does not hide the per-file rejection details.
        if (fileErrors.length > 0) {
          notify({
            id: NotificationKeys.ImportedInvoicesUploadRejection,
            message: fileErrors.join('\n'),
            title: t('file.rejected.count', { count: fileErrors.length }),
            variant: 'error',
          })
        }

        return
      }

      const totalFilesCount = filesAccepted.length
      let submittedCount = 0

      if (totalFilesCount > 0) {
        /* eslint-disable @typescript-eslint/naming-convention */
        track('External Invoice Uploaded (Client)', {
          external_invoice_file_count: totalFilesCount,
          external_invoice_is_auto_forward_to_cegid: isAutoCegidIntegrationEnabled,
        })
        /* eslint-enable @typescript-eslint/naming-convention */
      }

      for (const file of filesAccepted) {
        setUploadProgress((previousUploadProgress) => ({
          current: previousUploadProgress.current + 1,
          total: totalFilesCount,
        }))

        try {
          // Always mark the upload as an imported sales invoice (sets the
          // imported-sales-invoice origin + org scope on billy-api, no OCR scan,
          // no expenses attachment). The agentic pipeline is dispatched ONLY on the
          // M1b / non-autoforward path; shoebox auto-forward orgs store-only (marker
          // without dispatch) because rest-invoice forwards the document itself.
          const { files } = await uploadFile(file, {
            'x-organizationId': organization?.id,
            'x-imported-sales-invoice': true,
            ...(isAutoCegidIntegrationEnabled ? {} : { 'x-dispatch-agentic-document': true }),
          })

          // The stored File id is both the correlation key and the preview/forward
          // handle. Create the imported (shoebox) row in rest-invoice so it surfaces
          // on the imported-invoices page; auto-Cegid-eligible orgs get it forwarded.
          const fileId = files?.[0]?.id
          if (fileId) {
            await createImportedInvoice({
              fileId,
              filename: file.name,
              autoForwardToCegid: isAutoCegidIntegrationEnabled,
            })
          }
          submittedCount += 1
        } catch (error) {
          // This catch covers both uploadFile (billy-api APIError shape) and
          // createImportedInvoice (apiZervant/axios shape), so extract the server
          // message from either before falling back to the generic copy.
          const errorMessage = getImportedUploadErrorMessage(error)
          fileErrors.push(`${file.name}: ${errorMessage || t('file.rejected.unknown_error')}`)
        }
      }

      // Rejected (invalid) and failed-to-upload files share a dedicated toast id so
      // it is not dismissed by the upload success toast below.
      if (fileErrors.length > 0) {
        notify({
          id: NotificationKeys.ImportedInvoicesUploadRejection,
          message: fileErrors.join('\n'),
          title: t('file.rejected.count', { count: fileErrors.length }),
          variant: submittedCount > 0 ? 'warning' : 'error',
        })
      }

      // The file is queued for asynchronous extraction, so this toast confirms the
      // upload itself and nothing about the invoice's contents.
      if (submittedCount > 0) {
        notify({
          id: NotificationKeys.ImportedInvoicesUpload,
          message: t('imported_invoices.upload.submitted', { count: submittedCount }),
          variant: 'success',
        })

        // Invalidating the base key refetches the imported-invoices list query, which drives both the
        // table and the empty/list decision, so the newly submitted invoice(s) surface once the row
        // is created.
        queryClient.invalidateQueries([QueryKeys.ImportedInvoices])
      }
    },
    [isAutoCegidIntegrationEnabled, organization, queryClient, t, track],
  )

  const { isLoading, mutate: upload } = useMutation(handleMultipleFilesMutation, {
    onSettled: () => {
      setUploadProgress(uploadProgressInitialData)
    },
  })

  const uploadFiles = useCallback(
    (filesAccepted: File[], filesRejected?: FileRejection[]) => {
      upload({ filesAccepted, filesRejected })
    },
    [upload],
  )

  return {
    isUploading: isLoading,
    progress: uploadProgress,
    uploadFiles,
  }
}

function getRejectedFilesMessages(filesRejected: FileRejection[] | undefined, t: TFunction): string[] {
  return (filesRejected ?? []).map(
    (fileRejected) => `${fileRejected.file.name}: ${getImportedUploadRejectionMessage(fileRejected, t)}`,
  )
}

/**
 * The untitled oversize sentence can only stand in for a filename when there is
 * exactly one file in play. Requiring zero accepted files makes the shape decidable
 * from the arguments alone — nothing can succeed or fail server-side afterwards —
 * and keeps the missing-organization branch (which is guarded by accepted files) on
 * its existing grouped toast.
 */
function getLoneOversizeRejection(files: {
  filesAccepted: File[]
  filesRejected?: FileRejection[]
}): FileRejection | undefined {
  const { filesAccepted, filesRejected } = files

  if (filesAccepted.length > 0 || filesRejected?.length !== 1) {
    return undefined
  }

  const [fileRejected] = filesRejected

  return fileRejected.errors.some((error) => error.code === ErrorCode.FileTooLarge) ? fileRejected : undefined
}
